import SignUpServices from 'services/sign_up';
import { useEffect, useState } from 'react';
import { normalizeDealership } from '../Lookup/helpers';

export const AUCTION_ACCESS_COMPANY_VALIDATION_DEBOUNCE_MS = 500;
export const EIN_LAST_FOUR_LENGTH = 4;

const EIN_LAST_FOUR_PATTERN = /^\d{4}$/;

const isDealership = (value) =>
	value &&
	typeof value === 'object' &&
	[
		'auctionAccessDealerId',
		'auction_access_dealer_id',
		'companyCandidateId',
		'company_candidate_id',
		'name',
		'company_name',
		'legal_name',
		'address',
		'dealerLicense',
		'dealer_license',
	].some((field) => field in value);

const getValidatedDealership = (responseData, selectedCompanyCandidateId) => {
	const data = responseData?.data || responseData || {};
	const dealerships = Array.isArray(data.dealerships) ? data.dealerships : [];
	const dealership = [
		data.dealership,
		data.company,
		dealerships.find(
			(dealership) =>
				String(
					dealership?.companyCandidateId ||
						dealership?.company_candidate_id ||
						dealership?.auctionAccessDealerId ||
						dealership?.auction_access_dealer_id ||
						'',
				) === selectedCompanyCandidateId,
		),
		data,
	].find(isDealership);

	return dealership ? normalizeDealership(dealership) : undefined;
};

const useAuctionAccessCompanyValidation = ({ companyCandidateId, ein }) => {
	const [validatedDealership, setValidatedDealership] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState();
	const normalizedCompanyCandidateId = String(companyCandidateId || '').trim();
	const normalizedEin = typeof ein === 'string' ? ein.trim() : '';
	const hasValidEin = EIN_LAST_FOUR_PATTERN.test(normalizedEin);

	useEffect(() => {
		if (!normalizedCompanyCandidateId || !hasValidEin) {
			setValidatedDealership();
			setIsLoading(false);
			setError();
			return undefined;
		}

		let isActive = true;
		setValidatedDealership();
		setIsLoading(true);
		setError();

		const timeoutId = setTimeout(async () => {
			try {
				const { data } = await SignUpServices.instance.validateAuctionAccessCompany({
					companyCandidateId: normalizedCompanyCandidateId,
					last4GovernmentId: normalizedEin,
				});

				if (!isActive) return;
				setValidatedDealership(getValidatedDealership(data, normalizedCompanyCandidateId));
			} catch (requestError) {
				if (!isActive) return;
				setError(requestError);
				setValidatedDealership();
			} finally {
				if (isActive) setIsLoading(false);
			}
		}, AUCTION_ACCESS_COMPANY_VALIDATION_DEBOUNCE_MS);

		return () => {
			isActive = false;
			clearTimeout(timeoutId);
		};
	}, [hasValidEin, normalizedCompanyCandidateId, normalizedEin]);

	return { error, isLoading, validatedDealership };
};

export default useAuctionAccessCompanyValidation;
